<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import {
  Header,
  HeaderContent,
  HeaderTitle,
  HeaderMenu,
  HeaderMenuItem,
  HeaderMenuTrigger,
  HeaderMenuContent,
  HeaderMenuList,
  HeaderMenuLinkItem,
  HeaderMenuLink,
  HeaderMenuListItem,
  HeaderSpacer
} from '@toni/brand-ui'

import { usePageStore } from '../../stores/page'
import { useClient } from '../../utils/client-only'
import { type Header as AppHeaderType } from '../../utils/content-block-schema/header-schema'

const header = usePageStore().pageData.header as AppHeaderType
const { isClient } = useClient()
const { t } = useI18n()
</script>

<template>
  <Header data-testid="header"
          data-allow-mismatch>
    <HeaderTitle href="/"
                 :aria-label="t('ariaLabel.header.home')">
      <slot />
    </HeaderTitle>
    <!-- Server-side navigation (only visible when JS is disabled) -->
    <!-- eslint-disable vue/html-indent -->
    <noscript>
      <div class="flex-1">
        <nav :aria-label="t('ariaLabel.header.mainNavigation')">
          <ul class="flex gap-4 pt-3 text-button-md font-button">
            <li v-for="(item, index) in header.menu"
                :key="`menu-item-${index}`"
                class="relative">
              <a :href="item.url"
                 class="flex px-4 py-4 text-button-md font-button">
                {{ item.name }}
              </a>
              <ul v-if="item.children?.length"
                  class="absolute bg-white shadow-md mt-2 p-2 rounded text-button-sm text-primary-500"
                  :aria-label="t('ariaLabel.header.menuTrigger', { name: item.name })">
                <li v-for="(child, childIndex) in item.children"
                    :key="`submenu-item-${index}-${childIndex}`">
                  <a :href="child.url"
                     class="block px-4 py-2">
                    {{ child.name }}
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </div>
    </noscript>
    <!-- eslint-enable vue/html-indent -->
    <!-- Client-side enhanced navigation (only when JS is available) -->
    <HeaderContent v-if="isClient"
                   :aria-label="t('ariaLabel.header.content')">
      <HeaderMenu>
        <HeaderMenuList>
          <template v-for="(item, index) in header.menu">
            <HeaderMenuItem v-if="item.children?.length"
                            :key="`menu-item-${index}`">
              <HeaderMenuTrigger :url="item.url"
                                 :aria-label="t('ariaLabel.header.menuTrigger', { name: item.name })">
                {{ item.name }}
              </HeaderMenuTrigger>
              <HeaderMenuContent>
                <HeaderMenuListItem v-for="(child, childIndex) in item.children"
                                    :key="`menu-item-${index}-link-${childIndex}`"
                                    :url="child.url">
                  {{ child.name }}
                </HeaderMenuListItem>
              </HeaderMenuContent>
            </HeaderMenuItem>
            <HeaderMenuLinkItem v-else
                                :key="`menu-link-item-${index}-link`">
              <HeaderMenuLink :url="item.url">
                {{ item.name }}
              </HeaderMenuLink>
            </HeaderMenuLinkItem>
          </template>
        </HeaderMenuList>
      </HeaderMenu>
    </HeaderContent>
  </Header>
  <HeaderSpacer />
</template>
